import { useFormik } from 'formik';
import { FormikForm } from '@/components/form/FormikForm/FormikForm';
import { Button } from '@/components/Button/Button';
import { FormButtonBlockCentered } from '../../../../shared/ui/styles';
import { ResponseErrorType } from '@/types/sharedTypes';
import { formatPayloadDate } from '@/helpers/dateHelpers/dateHelpers';
import { FormikSelect } from '@/components/form/fields/FormikSelect/FormikSelect';
import { FormikInput } from '@/components/form/fields/FormikInput/FormikInput';
import { FormikDatePicker } from '@/components/form/fields/FormikDatePicker/FormikDatePicker';
import { FormikTextArea } from '@/components/form/fields/FormikTextArea/FormikTextArea';
import { OptionType } from '@/components/form/fields/Select/Select';
import { FieldRow } from '@/components/form/FieldRow/FieldRow';
import { formatNotes } from '@/helpers/formatHelpers';
import { getResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { Notification } from '@/components/Notification/Notification';
import { WireTypesType } from '@/bundle/_OrgAdmin/pages/TransactionDetailsPage/const/const';
import { CreateWirePayloadType } from '@/api/v1/organization/contracts/createWireApi';
import { WireAssignedType } from '@/types/wireTypes';
import { getCreateWireInitialValues, CREATE_WIRE_VALIDATION_SCHEMA, WIRES_OPTIONS } from './const';

type CreateWireFormType = {
  onCreate: (payload: CreateWirePayloadType) => void;
  currencyOptions: OptionType[];
  onClose: (reloadWires?: boolean) => void;
  selectedWireType: WireTypesType;
  isLoading: boolean;
  apiError: ResponseErrorType;
};

type CreateWireFormValuesType = {
  type: OptionType;
  name: string;
  amount?: string;
  currency: OptionType;
  expected_payment_date?: string | Date;
  notes?: string;
};

export const CreateWireForm = ({
  onCreate,
  onClose,
  currencyOptions = [],
  selectedWireType,
  isLoading,
  apiError,
}: CreateWireFormType) => {
  const formik = useFormik<CreateWireFormValuesType>({
    initialValues: getCreateWireInitialValues(currencyOptions, selectedWireType),
    validationSchema: CREATE_WIRE_VALIDATION_SCHEMA,
    enableReinitialize: true,
    onSubmit: (values) => {
      const { name, amount, currency, expected_payment_date, type, notes } = values;

      const payload: CreateWirePayloadType = {
        name,
        currency: currency.value as string,
        amount,
        expected_payment_date: expected_payment_date ? formatPayloadDate(expected_payment_date) : null,
        type: type.value as WireAssignedType,
        notes: formatNotes(notes),
      };

      onCreate(payload);
    },
  });

  const cancelHandler = () => {
    onClose();
  };

  const formError = getResponseError(apiError);

  return (
    <>
      {formError && (
        <Notification variant='error' marginBottom='16px'>
          {formError}
        </Notification>
      )}

      <FormikForm value={formik}>
        <FormikSelect
          name='type'
          label='Wire Type*'
          placeholder='Select Wire Type'
          options={WIRES_OPTIONS}
          apiError={apiError}
        />
        <FormikInput name='name' label='Wire Name*' placeholder='Enter Name' apiError={apiError} />
        <FieldRow columns={[7, 5]}>
          <FormikInput name='amount' label='Wire Amount' placeholder='Enter Amount' apiError={apiError} />
          <FormikSelect name='currency' label='Currency' options={currencyOptions} apiError={apiError} />
        </FieldRow>

        <FieldRow columns={[7, 5]}>
          <FormikDatePicker
            name='expected_payment_date'
            label='Expected Date'
            placeholder='Select Date'
            apiError={apiError}
          />
        </FieldRow>
        <FormikTextArea name='notes' label='Notes' placeholder='Enter Notes' apiError={apiError} />

        <FormButtonBlockCentered>
          <Button variant='secondary' mobileStretch onClick={cancelHandler} width={200}>
            Cancel
          </Button>
          <Button type='submit' mobileStretch isLoading={isLoading} width={200}>
            Save
          </Button>
        </FormButtonBlockCentered>
      </FormikForm>
    </>
  );
};
